'use client';

import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { Bookmark, Check, Loader2 } from 'lucide-react';

interface SaveRecipeButtonProps {
  blogPostId: string;
  className?: string;
}

export function SaveRecipeButton({ blogPostId, className = '' }: SaveRecipeButtonProps) {
  const { data: session, status } = useSession();
  const router = useRouter();
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);

  // Check if recipe is already saved
  useEffect(() => {
    checkSavedStatus();
  }, [blogPostId, session]);

  async function checkSavedStatus() {
    if (status === 'loading') return;
    
    setIsChecking(true);
    try {
      const response = await fetch(`/api/recipes/saved/check?blogPostId=${blogPostId}`);
      if (response.ok) {
        const data = await response.json();
        setIsSaved(data.isSaved);
      }
    } catch (error) {
      console.error('Error checking saved status:', error);
    } finally {
      setIsChecking(false);
    }
  }

  async function handleSave() {
    // Redirect to login if not authenticated
    if (!session) {
      router.push('/login?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }

    setIsLoading(true);

    try {
      if (isSaved) {
        // Unsave
        const response = await fetch('/api/recipes/saved', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ blogPostId }),
        });

        if (response.ok) {
          setIsSaved(false);
        } else {
          throw new Error('Failed to unsave recipe');
        }
      } else {
        // Save
        const response = await fetch('/api/recipes/saved', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ blogPostId }),
        });

        if (response.ok) {
          setIsSaved(true);
          // Show success animation
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 2000);
        } else {
          throw new Error('Failed to save recipe');
        }
      }
    } catch (error) {
      console.error('Error toggling save:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  if (isChecking) {
    return null; // Or a skeleton loader
  }

  return (
    <div className={className}>
      <button
        onClick={handleSave}
        disabled={isLoading}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.625rem',
          padding: '1rem 2rem',
          background: isSaved
            ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: isSaved
            ? '2px solid rgba(16, 185, 129, 0.5)'
            : '2px solid rgba(217, 119, 6, 0.3)',
          borderRadius: '20px',
          color: isSaved ? 'white' : '#D97706',
          fontSize: '1rem',
          fontWeight: 700,
          cursor: isLoading ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: isSaved
            ? '0 8px 24px rgba(16, 185, 129, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.5) inset'
            : '0 8px 24px rgba(217, 119, 6, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
          fontFamily: 'inherit',
          overflow: 'hidden',
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
            e.currentTarget.style.boxShadow = isSaved
              ? '0 12px 32px rgba(16, 185, 129, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.5) inset'
              : '0 12px 32px rgba(217, 119, 6, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.5) inset';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = isSaved
            ? '0 8px 24px rgba(16, 185, 129, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.5) inset'
            : '0 8px 24px rgba(217, 119, 6, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.5) inset';
        }}
      >
        {/* Success Animation Overlay */}
        {showSuccess && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle, rgba(255, 255, 255, 0.3), transparent 70%)',
              animation: 'successPulse 0.6s ease-out',
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Icon */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isLoading ? (
            <Loader2
              size={22}
              style={{
                animation: 'spin 1s linear infinite',
              }}
            />
          ) : isSaved ? (
            <Check
              size={22}
              style={{
                animation: showSuccess ? 'checkBounce 0.6s ease-out' : 'none',
              }}
            />
          ) : (
            <Bookmark size={22} />
          )}
        </div>

        {/* Text */}
        <span>{isLoading ? 'Saving...' : isSaved ? 'Saved!' : 'Save Recipe'}</span>

        {/* Shimmer Effect for unsaved state */}
        {!isSaved && !isLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
              animation: 'shimmer 3s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />
        )}
      </button>

      <style jsx>{`
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 200%; }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes successPulse {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          50% {
            opacity: 1;
            transform: scale(1.1);
          }
          100% {
            opacity: 0;
            transform: scale(1.2);
          }
        }

        @keyframes checkBounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}

